import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import useSound from "use-sound";
import Popover from "@material-ui/core/Popover";
import { makeStyles } from "@material-ui/core/styles";
import openSocket from "../../services/socket-io";
import {
	connectSocket,
	getGoWebSocket,
	addGoWebSocketMessageHandler,
	addGoWebSocketOpenHandler,
} from "../../services/socketv2";
import NotificationTicketItem from "./NotificationTicketItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/som_whatsapp.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isStatusTicketRecord } from "../../helpers/isStatusTicket";
import { isSameTicketCompany } from "../../helpers/ticketSocketRoom";
import { TICKET_REALTIME } from "../../helpers/ticketRealtimeEvents";
import { isNotificationSoundMuted } from "../../helpers/notificationSoundPreference";

function IconBellRing() {
	return (
		<svg className="size-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M10.268 21a2 2 0 0 0 3.464 0" />
			<path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
			<path d="M12 2v1" />
			<path d="M2 5.5 5 7.5" />
			<path d="M22 5.5 19 7.5" />
		</svg>
	);
}

const useStyles = makeStyles((theme) => ({
	popoverPaper: {
		width: 380,
		maxWidth: "calc(100vw - 24px)",
		minWidth: 300,
		marginTop: 8,
		borderRadius: 8,
		overflow: "hidden",
		boxShadow: "0 4px 24px rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.background.paper,
		[theme.breakpoints.down("sm")]: {
			width: 300,
			minWidth: 280,
		},
	},
}));

function NotificationsPopOver({ variant = "topbar" }) {
	const classes = useStyles();
	const isTopbar = variant === "topbar";
	const history = useHistory();
	const { user, setIsSocketConnected } = useContext(AuthContext);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const [anchorEl, setAnchorEl] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [, setDesktopNotifications] = useState([]);
	const { tickets } = useTickets({
		pageNumber: "1",
		withUnreadMessages: "true",
	});
	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();
	const historyRef = useRef(history);
	const notifiedMessageIdsRef = useRef(new Set());

	const count = notifications.length;
	const countLabel = count > 99 ? "99+" : String(count);

	useEffect(() => {
		soundAlertRef.current = play;
		if ("Notification" in window) {
			Notification.requestPermission();
		}
	}, [play]);

	// Mesmas regras de setor/grupo do buildTicketsQuery (Go).
	const canSeeTicket = useCallback(
		(ticket) => {
			if (!ticket || !user) return false;
			if (!isSameTicketCompany(ticket, user.companyId)) return false;
			if (!user.canSeeGroups && ticket.isGroup) return false;
			const userQueueIds = (user.queues || []).map((q) => Number(q.id));
			if (userQueueIds.length === 0) return true;
			if (ticket.queueId == null) return Boolean(user.canSeeAllTickets);
			return userQueueIds.includes(Number(ticket.queueId));
		},
		[user]
	);

	const isRelevantNotificationTicket = useCallback(
		(ticket) => {
			if (!ticket || (ticket.unreadMessages || 0) <= 0) {
				return false;
			}
			if (ticket.status === "closed" || isStatusTicketRecord(ticket)) {
				return false;
			}
			if (ticket.status !== "pending") {
				return false;
			}
			return canSeeTicket(ticket);
		},
		[canSeeTicket]
	);

	useEffect(() => {
		if (!user) return;
		setNotifications(tickets.filter(isRelevantNotificationTicket));
	}, [tickets, user, isRelevantNotificationTicket]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		if (!user?.id) return undefined;
		const socket = openSocket({
			scope: "notifications",
			userId: user.id,
			component: "NotificationsPopOver",
			companyId: user.companyId ?? "",
		});
		if (!socket) return undefined;

		socket.on("customDisconnectEvent", () => {
			setIsSocketConnected(false);
		});

		socket.on("connect", () => {
			setIsSocketConnected(true);
			socket.emit("joinNotification");
		});

		socket.on("ticket", (data) => {
			if (!data.ticket) return;
			if (!canSeeTicket(data.ticket)) return;

			const removeNotificationForTicket = (ticketId) => {
				setNotifications((prevState) => {
					const ticketIndex = prevState.findIndex((t) => t.id === ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications((prevState) => {
					const notfiticationIndex = prevState.findIndex(
						(n) => n.tag === String(ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			};

			if (data.action === "updateUnread" || data.action === "delete") {
				removeNotificationForTicket(data.ticket?.id);
				return;
			}

			if (data.action === "update") {
				const ticket = data.ticket;
				if (
					!ticket ||
					ticket.status === "closed" ||
					(ticket.unreadMessages || 0) <= 0
				) {
					removeNotificationForTicket(ticket?.id);
					return;
				}
				if (!isRelevantNotificationTicket(ticket)) {
					removeNotificationForTicket(ticket.id);
					return;
				}
				setNotifications((prevState) => {
					const ticketIndex = prevState.findIndex((t) => t.id === ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = ticket;
						return [...prevState];
					}
					return [ticket, ...prevState];
				});
			}
		});

		const handleIncomingMessage = (data) => {
			if (!data?.ticket || !data.message) return;
			if (data.message.fromMe) return;
			if (!canSeeTicket(data.ticket)) return;

			// O Go emite a mesma mensagem em várias salas (ticket, status, notificação).
			const messageId = data.message.id;
			if (messageId) {
				const seen = notifiedMessageIdsRef.current;
				if (seen.has(messageId)) return;
				seen.add(messageId);
				if (seen.size > 500) {
					seen.delete(seen.values().next().value);
				}
			}

			if (
				data.action === "create" &&
				!data.message.read &&
				(data.ticket.userId === user?.id || !data.ticket.userId)
			) {
				if (
					data.ticket.status === "closed" ||
					(data.ticket.unreadMessages || 0) <= 0
				) {
					return;
				}
				setNotifications((prevState) => {
					const ticketIndex = prevState.findIndex((t) => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id);

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		};

		socket.on("appMessage", handleIncomingMessage);

		const handleGoSocketMessage = (event) => {
			let data;
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}
			if (data?.action !== "create") return;
			handleIncomingMessage(data);
		};

		const joinGoNotificationRoom = () => {
			const s = getGoWebSocket();
			if (!s || s.readyState !== WebSocket.OPEN) return;
			s.send(JSON.stringify({ action: "joinTickets", room: "notification" }));
		};

		const removeGoMessage = addGoWebSocketMessageHandler(handleGoSocketMessage);
		const removeGoOpen = addGoWebSocketOpenHandler(joinGoNotificationRoom);
		connectSocket({ userId: user.id, companyId: user.companyId });
		joinGoNotificationRoom();

		const handleTicketRealtime = (event) => {
			const data = event?.detail;
			if (!data?.ticket || !isSameTicketCompany(data.ticket, user.companyId)) {
				return;
			}
			const ticket = data.ticket;
			const shouldRemove =
				data.action === "delete" ||
				data.action === "updateUnread" ||
				ticket.status !== "pending" ||
				(ticket.unreadMessages || 0) <= 0 ||
				!isRelevantNotificationTicket(ticket);

			if (!shouldRemove) return;

			setNotifications((prevState) =>
				prevState.filter((t) => t.id !== ticket.id)
			);
			setDesktopNotifications((prevState) => {
				const idx = prevState.findIndex((n) => n.tag === String(ticket.id));
				if (idx !== -1) {
					prevState[idx].close();
					prevState.splice(idx, 1);
					return [...prevState];
				}
				return prevState;
			});
		};

		window.addEventListener(TICKET_REALTIME, handleTicketRealtime);

		return () => {
			window.removeEventListener(TICKET_REALTIME, handleTicketRealtime);
			removeGoMessage();
			removeGoOpen();
			socket.disconnect();
		};
	}, [user?.id, user?.companyId, setIsSocketConnected, isRelevantNotificationTicket, canSeeTicket]);

	const handleNotifications = (data) => {
		const { message, ticket } = data;
		const contact = data.contact || ticket.contact || {};

		if (!ticket.isStatus && !isNotificationSoundMuted(user?.id)) {
			soundAlertRef.current?.();
		}

		if (!("Notification" in window) || Notification.permission !== "granted") {
			return;
		}

		const options = {
			body: `${message.body || ""} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: String(ticket.id),
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name || ""}`,
			options
		);

		notification.onclick = (e) => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications((prevState) => {
			const notfiticationIndex = prevState.findIndex((n) => n.tag === notification.tag);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});
	};

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
		setIsOpen((prev) => !prev);
	};

	const handleClose = () => {
		setIsOpen(false);
		setAnchorEl(null);
	};

	const label = i18n.t("mainDrawer.listItems.tickets");

	return (
		<>
			<div
				className={
					isTopbar
						? "topbar-item app-topbar-notifications relative inline-flex"
						: "hidden"
				}
			>
				<button
					type="button"
					className="btn btn-icon relative size-8 rounded-full text-default-600 hover:bg-default-150 dark:text-default-300"
					onClick={handleClick}
					aria-label={label}
					title={label}
					aria-expanded={isOpen}
				>
					<IconBellRing />
					{count > 0 && (
						<span className="absolute -end-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white">
							{countLabel}
						</span>
					)}
				</button>
			</div>

			<Popover
				disableScrollLock
				disableRestoreFocus
				open={isOpen}
				anchorEl={anchorEl}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				classes={{ paper: classes.popoverPaper }}
				className="notifications-popover"
				onClose={handleClose}
			>
				<div className="flex items-center gap-2 border-b border-default-200 px-4 py-3 dark:border-default-700">
					<h3 className="m-0 text-base font-semibold text-default-800 dark:text-default-100">
						Notificações
					</h3>
					{count > 0 && (
						<span className="flex size-5 items-center justify-center rounded bg-orange-500 text-xs font-semibold text-white">
							{countLabel}
						</span>
					)}
				</div>
				<div className="notifications-popover__list max-h-80 overflow-y-auto">
					{notifications.length === 0 ? (
						<p className="m-0 px-4 py-6 text-center text-sm text-default-500">
							{i18n.t("notifications.noTickets")}
						</p>
					) : (
						notifications.map((ticket) => (
							<NotificationTicketItem
								key={ticket.id}
								ticket={ticket}
								onNavigate={handleClose}
							/>
						))
					)}
				</div>
			</Popover>
		</>
	);
}

export default NotificationsPopOver;
